import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

type CourseItem = {
  id: number;
  category_id?: number | null;
  category: string;
  name: string;
  instructor: string;
  department: string;
  assigned_user: string;
  status: "published" | "private";
  students: number;
  thumbnail?: string | null;
  video_url?: string | null;
  ppt_url?: string | null;
  ppt_preview_url?: string | null;
  ppt_slide_urls?: string | null;
  pdf_url?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  certificate_enabled?: boolean;
  reward_enabled?: boolean;
};

type CourseCategoryItem = {
  id: number;
  title: string;
  description?: string | null;
  thumbnail?: string | null;
  allowed_departments: string[];
  is_active?: boolean;
};

function CourseEditPage() {
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [course, setCourse] = useState<CourseItem | null>(null);
  const [categories, setCategories] = useState<CourseCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [coursesResponse, categoriesResponse] = await Promise.all([
        fetch("/courses/"),
        fetch("/course-categories/"),
      ]);

      if (!coursesResponse.ok) {
        throw new Error("Failed to fetch courses");
      }

      if (!categoriesResponse.ok) {
        throw new Error("Failed to fetch course categories");
      }

      const coursesData: CourseItem[] = await coursesResponse.json();
      const categoriesData: CourseCategoryItem[] =
        await categoriesResponse.json();

      const targetCourse = coursesData.find(
        (item) => item.id === Number(courseId)
      );

      if (!targetCourse) {
        throw new Error("Course not found");
      }

      setCourse(targetCourse);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching course edit data:", error);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mainCategory = useMemo(() => {
    if (!course) return null;

    return categories.find((category) => category.title === course.category);
  }, [categories, course]);

  const mainCategoryDepartments = useMemo(() => {
    if (!mainCategory || mainCategory.allowed_departments.length === 0) {
      return "未設定";
    }

    return mainCategory.allowed_departments.join(", ");
  }, [mainCategory]);

  const handleFileUpload = async (
    file: File,
    fieldName: "thumbnail" | "video_url" | "ppt_url" | "pdf_url"
  ) => {
    if (!course) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/courses/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

            const data: {
        url: string;
        ppt_preview_url?: string | null;
      } = await response.json();

      if (fieldName === "ppt_url") {
        setCourse({
          ...course,
          ppt_url: data.url,
          ppt_preview_url: data.ppt_preview_url || null,
        });
      } else {
        setCourse({
          ...course,
          [fieldName]: data.url,
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("ファイルのアップロードに失敗しました。");
    }
  };

  const handleUpdateCourse = async () => {
    if (!course) return;

    if (!course.name.trim()) {
      alert("コース名を入力してください。");
      return;
    }

    try {
      const updatedCourse = {
        category: course.category,
        category_id: course.category_id ?? mainCategory?.id ?? null,
        name: course.name,
        instructor: course.instructor,
        department: mainCategoryDepartments === "未設定" ? "" : mainCategoryDepartments,
        assigned_user: course.assigned_user,
        status: course.status,
        students: course.students,
        thumbnail: course.thumbnail || null,
        video_url: course.video_url || null,
        ppt_url: course.ppt_url || null,
        ppt_preview_url: course.ppt_preview_url || null,
        ppt_slide_urls: course.ppt_slide_urls || null,
        pdf_url: course.pdf_url || null,
        start_date: course.start_date || null,
        end_date: course.end_date || null,
        certificate_enabled: course.certificate_enabled || false,
        reward_enabled: course.reward_enabled || false,
      };

      const response = await fetch(
        `/courses/${course.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedCourse),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update course");
      }

      alert("コースを更新しました。");

      if (mainCategory) {
  navigate(
    `/courses/category/${mainCategory.id}?title=${encodeURIComponent(
      mainCategory.title
    )}`
  );
} else {
  navigate("/courses");
}
    } catch (error) {
      console.error("Error updating course:", error);
      alert("コースの更新に失敗しました。");
    }
  };

  if (loading) {
    return <div style={loadingStyle}>読み込み中...</div>;
  }

  if (!course) {
    return (
      <div style={pageStyle}>
        <h2>コースが見つかりません</h2>
        <button onClick={() => navigate("/courses")} style={cancelButtonStyle}>
          コース管理へ戻る
        </button>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>コース編集</h1>
          <p style={subtitleStyle}>サブコース情報を編集します</p>
        </div>

        <div style={headerButtonAreaStyle}>
          <button onClick={() => navigate(-1)} style={cancelButtonStyle}>
            ← 戻る
          </button>

          <button onClick={handleUpdateCourse} style={primaryButtonStyle}>
            更新する
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={topGridStyle}>
          <div>
            <label style={labelStyle}>カテゴリー</label>
            <input
              type="text"
              value={course.category}
              readOnly
              style={{ ...inputStyle, backgroundColor: "#f8fafc" }}
            />
          </div>

          <div>
            <label style={labelStyle}>コース名</label>
            <input
              type="text"
              value={course.name}
              onChange={(e) => setCourse({ ...course, name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>講師名</label>
            <input
              type="text"
              value={course.instructor}
              onChange={(e) =>
                setCourse({ ...course, instructor: e.target.value })
              }
              style={inputStyle}
            />
          </div>
        </div>

        <div style={readOnlyInfoBoxStyle}>
          <div>
            <strong>対象部署：</strong>
            <span>{mainCategoryDepartments}</span>
          </div>
          <p style={helperTextStyle}>
            対象部署はメインカテゴリー側で管理します。
          </p>
        </div>

        <div style={fileGridStyle}>
          <div>
            <label style={labelStyle}>サムネイル画像</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, "thumbnail");
              }}
              style={inputStyle}
            />
            {course.thumbnail && (
              <p style={fileLinkTextStyle}>現在のファイル: {course.thumbnail}</p>
            )}
          </div>

          <div>
            <label style={labelStyle}>動画</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, "video_url");
              }}
              style={inputStyle}
            />
            {course.video_url && (
              <p style={fileLinkTextStyle}>現在のファイル: {course.video_url}</p>
            )}
          </div>

          <div>
            <label style={labelStyle}>PPT資料</label>
            <input
              type="file"
              accept=".ppt,.pptx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, "ppt_url");
              }}
              style={inputStyle}
            />
            {course.ppt_url && (
              <p style={fileLinkTextStyle}>現在のファイル: {course.ppt_url}</p>
            )}
          </div>

          <div>
            <label style={labelStyle}>PDF資料</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, "pdf_url");
              }}
              style={inputStyle}
            />
            {course.pdf_url && (
              <p style={fileLinkTextStyle}>現在のファイル: {course.pdf_url}</p>
            )}
          </div>
        </div>

        <div style={dateGridStyle}>
          <div>
            <label style={labelStyle}>開始日</label>
            <input
              type="date"
              value={course.start_date || ""}
              onChange={(e) =>
                setCourse({ ...course, start_date: e.target.value })
              }
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>終了日</label>
            <input
              type="date"
              value={course.end_date || ""}
              onChange={(e) =>
                setCourse({ ...course, end_date: e.target.value })
              }
              style={inputStyle}
            />
          </div>
        </div>

        
        

        <div>
          <label style={labelStyle}>ステータス</label>
          <select
            value={course.status}
            onChange={(e) =>
              setCourse({
                ...course,
                status: e.target.value as "published" | "private",
              })
            }
            style={inputStyle}
          >
            <option value="private">非公開</option>
            <option value="published">公開</option>
          </select>
        </div>
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  padding: "40px",
  backgroundColor: "#f8fafc",
  minHeight: "100vh",
};

const loadingStyle: React.CSSProperties = {
  padding: "40px",
  fontSize: "20px",
  fontWeight: 600,
};

const headerStyle: React.CSSProperties = {
  marginBottom: "32px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  fontSize: "52px",
  fontWeight: 800,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle: React.CSSProperties = {
  marginTop: "10px",
  color: "#64748b",
  fontSize: "28px",
};

const headerButtonAreaStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "24px",
  padding: "32px",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
};

const topGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "22px",
  marginBottom: "24px",
};

const fileGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "24px",
  marginBottom: "24px",
};

const dateGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "24px",
  marginBottom: "24px",
};

const readOnlyInfoBoxStyle: React.CSSProperties = {
  marginBottom: "24px",
  padding: "16px 18px",
  borderRadius: "16px",
  backgroundColor: "#f0fdfa",
  border: "1px solid #ccfbf1",
  color: "#0f172a",
  fontSize: "15px",
};

const optionBoxStyle: React.CSSProperties = {
  marginBottom: "24px",
  padding: "16px 18px",
  borderRadius: "16px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const checkboxOptionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontWeight: 700,
  color: "#0f172a",
  cursor: "pointer",
};

const fileLinkTextStyle: React.CSSProperties = {
  marginTop: "8px",
  marginBottom: 0,
  fontSize: "13px",
  color: "#2563eb",
  wordBreak: "break-all",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "8px",
  fontWeight: 700,
  color: "#0f172a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  fontSize: "16px",
  outline: "none",
  boxSizing: "border-box",
};

const helperTextStyle: React.CSSProperties = {
  marginTop: "8px",
  marginBottom: 0,
  fontSize: "13px",
  color: "#64748b",
};

const cancelButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 700,
  cursor: "pointer",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "12px 22px",
  borderRadius: "14px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 10px 24px rgba(15, 118, 110, 0.22)",
};

export default CourseEditPage;